// Single source of truth for per-route page metadata.
//
// Two consumers read this module and must never disagree:
//
//   1. use-document-meta.js, called by each page component. Updates the
//      live document after hydration. Covers humans and crawlers that run
//      JS (Google).
//   2. middleware.js, which injects the same values into the HTML before
//      it is served. Covers crawlers that do not run JS (LinkedIn, Slack,
//      iMessage, the classic Twitter scraper).
//
// A title or description that lives in a component, or in index.html, will
// eventually disagree with the other copy of itself. Put it here instead.
//
// Must stay dependency-free and free of DOM and Node APIs: middleware runs
// on the edge runtime, where neither exists.

export const SITE = 'https://www.margin.consulting';

// Served in index.html and used for any route without an entry below.
export const DEFAULTS = {
  title: 'Margin · Built to be handed off.',
  description:
    'I get handed the things nobody can describe, build the system that makes the problem tractable, train the people who will own it, and leave. Four engagements, four industries, same motion.',
  ogDescription:
    'Systems built and handed off. Four engagements across themed entertainment, cruise, civic infrastructure, and small business.',
  type: 'website',
  image: null,
};

// Static routes. `description` is the search-result description;
// `ogDescription` is the shorter line social cards use. Omit ogDescription
// to reuse description for both.
const ROUTES = {
  '/': {
    title: DEFAULTS.title,
    description: DEFAULTS.description,
    ogDescription: DEFAULTS.ogDescription,
    type: 'website',
  },
  '/intake': {
    title: 'Tell me about your business · Margin',
    description:
      'A ninety minute working session that maps how your business actually runs and shows you where the drag is. Not a sales call. Answer the questions and book a time.',
    type: 'website',
  },
  '/case-studies': {
    title: 'Case studies · Margin',
    description:
      'How we work, told through the businesses we work with. Diagnosis-led narratives, the math behind the calls we make, and what we build together.',
    type: 'website',
  },
  '/who-we-are': {
    title: 'About · Margin',
    description:
      "Who you're hiring. Jason Lyons has run an independent practice since 1999, building operating systems for organizations that have been handed something nobody has scoped.",
    type: 'website',
  },
};

// Normalizes a pathname: strips a trailing slash so '/intake/' and
// '/intake' resolve to the same entry. Root stays '/'.
export function normalizePath(pathname) {
  if (!pathname) return '/';
  const p = pathname.replace(/\/+$/, '');
  return p === '' ? '/' : p;
}

export function absoluteUrl(pathOrUrl) {
  if (!pathOrUrl) return SITE + '/';
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  return SITE + (pathOrUrl.startsWith('/') ? '' : '/') + pathOrUrl;
}

// Resolves metadata for a static route. Returns null for paths this module
// does not own (case study detail pages, diagnosis pages), so the caller
// can decide what to do instead of silently serving homepage meta.
export function metaForPath(pathname) {
  const path = normalizePath(pathname);
  const entry = ROUTES[path];
  if (!entry) return null;
  return resolve(entry, path);
}

// Metadata for one case study detail page. `row` is the /api/get-case-study
// response shape, or anything with the same display_* fields. Tolerates a
// null row (still loading, or lookup failed) and falls back to a generic
// case study page.
export function caseStudyMeta(row, slug) {
  const path = '/case-studies/' + slug;
  if (!row) {
    return resolve(
      {
        title: 'Case study · Margin',
        description: 'A Margin case study.',
        type: 'article',
      },
      path,
    );
  }
  // Title off display_name, not display_business. display_name is the
  // recognizable thing a person would search for ("Bakowski Bridge of
  // Lights"); display_business is a category ("Public infrastructure ·
  // lighting program") and makes a poor search result and a worse unfurl.
  //
  // Description is the authored summary, which is written as prose and is
  // already approved copy. The previous version interpolated display_city
  // into a parenthetical, which produced "(Withheld)" and "(Fleet-wide)" as
  // though they were places, and started the sentence mid-clause with a
  // capitalized article. Falls back to hero_kicker for rows with no summary.
  const blurb = (row.summary || row.hero_kicker || '').trim();
  return resolve(
    {
      title: row.display_name + ' · Case study · Margin',
      description: truncate(blurb, 160) || 'A Margin case study.',
      // 300 keeps the longest summary inside what a card actually renders,
      // so the ellipsis is ours rather than a platform's hard cut mid-word.
      ogDescription: truncate(blurb, 300) || 'A Margin case study.',
      type: 'article',
    },
    path,
  );
}

// Article JSON-LD for a case study. Used by CaseStudySchema.jsx in the
// browser and available to middleware.js for bots that never hydrate.
export function caseStudySchema(row, slug) {
  if (!row) return null;
  const url = absoluteUrl('/case-studies/' + slug);
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    // Matches the <title>, which is built off display_name for the same
    // reason: display_business is a category, not a name.
    headline: row.display_name + ' · Case study',
    url,
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    author: { '@type': 'Person', name: 'Jason Lyons' },
    publisher: { '@type': 'Organization', name: 'Margin', url: SITE + '/' },
    isAccessibleForFree: true,
  };
  const blurb = (row.summary || row.hero_kicker || '').trim();
  if (blurb) schema.description = truncate(blurb, 300);
  if (row.published_at) schema.datePublished = row.published_at;

  // display_city is a display string, not a place. Anonymized rows put
  // sentinels in it, and emitting Place{name:"Withheld"} tells Google the
  // article is located somewhere called Withheld, which is worse than
  // saying nothing. Only claim a location when one was actually given.
  if (isRealPlace(row.display_city)) {
    schema.contentLocation = { '@type': 'Place', name: row.display_city };
  }
  return schema;
}

const NOT_PLACES = new Set(['withheld', 'fleet-wide', 'fleetwide', 'various', 'n/a', 'anonymized', 'undisclosed']);

function isRealPlace(city) {
  const v = (city || '').trim();
  return v !== '' && !NOT_PLACES.has(v.toLowerCase());
}

// Trims to a word boundary under `max`, for the search-snippet description.
// Social cards get the untruncated line, so this only applies to `description`.
export function truncate(s, max) {
  const text = (s || '').replace(/\s+/g, ' ').trim();
  if (text.length <= max) return text;
  const cut = text.slice(0, max - 1);
  const at = cut.lastIndexOf(' ');
  return (at > max * 0.6 ? cut.slice(0, at) : cut).replace(/[,;:.\s]+$/, '') + '…';
}

function resolve(entry, path) {
  return {
    title: entry.title,
    description: entry.description,
    ogDescription: entry.ogDescription || entry.description,
    type: entry.type || 'website',
    image: entry.image || null,
    path,
    url: absoluteUrl(path),
  };
}
